import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAbortError, starchartApiService } from '../services/starchartApi';
import { starchartRankHistoryApiService } from '../services/starchartRankHistoryApi';
import { serverListApiService, type ServerListEntry } from '../services/serverListApi';
import { POPULARITY_WINDOWS } from '../types/starchart';
import type {
  MsptTimeSeriesResponse,
  PopularityWindow,
  RankHistoryResponse,
  ServerAggregate,
  StarchartMetric,
  StarchartServedResolution,
  StarchartServerStats,
  StarchartSummaryResponse,
  TimeSeriesResponse,
} from '../types/starchart';

export interface UseStarchartServerResult {
  timeseries: TimeSeriesResponse | null;
  mspt: MsptTimeSeriesResponse | null;
  summary: StarchartSummaryResponse | null;
  stats: StarchartServerStats | null;
  rankHistory: RankHistoryResponse | null;
  /** This server's current index and position in each ranking window. */
  standing: Partial<Record<PopularityWindow, { index: number; rank: number }>>;
  /** Sibling regions in the same cluster, with their player history for comparison. */
  siblings: ServerAggregate[];
  catalog: ServerListEntry | null;
  siblingFqdns: string[];
  loading: boolean;
  error: string | null;
  loadedAt: number | null;
  /** True when nothing upstream has ever heard of this id. */
  notFound: boolean;
  refresh: () => Promise<void>;
}

/**
 * Load one server's history.
 *
 * Requests are issued together and settled independently: `/servers/{id}/summary` answers HTTP
 * 500 for an unknown id while `/server/{id}/stats` answers 200 with nulls, so a page that waited
 * on all four would blank out over a single upstream quirk. Whatever resolves gets rendered.
 */
export function useStarchartServer(
  serverId: string,
  start: Date,
  end: Date,
  resolution: StarchartServedResolution,
  metric: StarchartMetric,
  popularityWindow: PopularityWindow,
): UseStarchartServerResult {
  const [timeseries, setTimeseries] = useState<TimeSeriesResponse | null>(null);
  const [mspt, setMspt] = useState<MsptTimeSeriesResponse | null>(null);
  const [summary, setSummary] = useState<StarchartSummaryResponse | null>(null);
  const [stats, setStats] = useState<StarchartServerStats | null>(null);
  const [rankHistory, setRankHistory] = useState<RankHistoryResponse | null>(null);
  const [standing, setStanding] = useState<
    Partial<Record<PopularityWindow, { index: number; rank: number }>>
  >({});
  const [siblings, setSiblings] = useState<ServerAggregate[]>([]);
  const [catalog, setCatalog] = useState<ServerListEntry | null>(null);
  const [siblingFqdns, setSiblingFqdns] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  // Tick timings are only fetched when a tick metric is selected, so switching from Players to
  // MSPT costs one request and switching back costs none.
  const needsMspt = metric === 'mspt' || metric === 'tps';

  const load = useCallback(
    async (force = false) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const { signal } = controller;

      setLoading(true);
      setError(null);

      try {
        const [seriesResult, summaryResult, statsResult, catalogResult] = await Promise.allSettled([
          starchartApiService.getServerTimeseries(serverId, start, end, resolution, {
            signal,
            force,
          }),
          starchartApiService.getServerSummary(serverId, start, end, { signal, force }),
          starchartApiService.getServerStats(serverId, { signal, force }),
          serverListApiService.getServerByFqdn(serverId),
        ]);

        if (signal.aborted) return;

        const series = seriesResult.status === 'fulfilled' ? seriesResult.value : null;
        const serverStats = statsResult.status === 'fulfilled' ? statsResult.value : null;
        const entry = catalogResult.status === 'fulfilled' ? (catalogResult.value ?? null) : null;

        setTimeseries(series);
        setSummary(summaryResult.status === 'fulfilled' ? summaryResult.value : null);
        setStats(serverStats);
        setCatalog(entry);
        setLoadedAt(Date.now());

        // A typo'd FQDN does not 404 anywhere: it returns empty history and null scores. Only
        // when nothing at all knows the id do we call it unknown.
        const hasHistory = (series?.data.length ?? 0) > 0;
        const hasStats = (serverStats?.snapshot_count ?? 0) > 0;
        setNotFound(!hasHistory && !hasStats && !entry);

        if (seriesResult.status === 'rejected' && !hasStats && !entry) {
          setError(
            seriesResult.reason instanceof Error
              ? seriesResult.reason.message
              : 'Failed to load server history',
          );
        }

        setLoading(false);

        // Enrichment below — never blocks first paint, never surfaces as a page error.
        const siblingAddresses = (entry?.clusterChildren ?? [])
          .map((child) => child.serverAddress)
          .filter((address) => address.toLowerCase() !== serverId.toLowerCase());
        setSiblingFqdns(siblingAddresses);

        // Rankings are small and shared with the overview's cache, so all three windows cost
        // little and give the page a position, not just an index.
        const rankingResults = await Promise.allSettled(
          POPULARITY_WINDOWS.map((window) =>
            starchartApiService.getRankings(window, { signal }).then((response) => ({
              window,
              response,
            })),
          ),
        );

        if (signal.aborted) return;

        const nextStanding: Partial<Record<PopularityWindow, { index: number; rank: number }>> = {};
        for (const result of rankingResults) {
          if (result.status !== 'fulfilled') continue;
          // Exact match: two servers can differ only by case and rank very differently.
          const position = result.value.response.ranked_list.findIndex(
            (item) => item.server_id === serverId,
          );
          // Absent from a window is meaningful: the server was too quiet to be ranked in it.
          if (position >= 0) {
            nextStanding[result.value.window] = {
              index: result.value.response.ranked_list[position].popularity_index,
              rank: position + 1,
            };
          }
        }
        setStanding(nextStanding);

        const [siblingResult, rankResult] = await Promise.allSettled([
          siblingAddresses.length > 0
            ? starchartApiService.getAggregate(siblingAddresses, start, end, resolution, { signal })
            : Promise.resolve([] as ServerAggregate[]),
          starchartRankHistoryApiService.getRankHistory(
            serverId,
            popularityWindow,
            start,
            end,
            signal,
          ),
        ]);

        if (signal.aborted) return;

        setSiblings(siblingResult.status === 'fulfilled' ? siblingResult.value : []);
        // Absent until argus-backend has been collecting; the UI says so rather than erroring.
        setRankHistory(rankResult.status === 'fulfilled' ? rankResult.value : null);
      } catch (err) {
        if (isAbortError(err) || signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load server data');
        setLoading(false);
      }
    },
    [serverId, start, end, resolution, popularityWindow],
  );

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  useEffect(() => {
    if (!needsMspt) return;

    const controller = new AbortController();
    starchartApiService
      .getServerMspt(serverId, start, end, resolution, { signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) setMspt(response);
      })
      .catch((err) => {
        if (!isAbortError(err)) setMspt(null);
      });

    return () => controller.abort();
  }, [needsMspt, serverId, start, end, resolution]);

  const refresh = useCallback(async () => {
    starchartApiService.clearCache();
    await load(true);
  }, [load]);

  return useMemo(
    () => ({
      timeseries,
      mspt,
      summary,
      stats,
      rankHistory,
      standing,
      siblings,
      catalog,
      siblingFqdns,
      loading,
      error,
      loadedAt,
      notFound,
      refresh,
    }),
    [
      timeseries,
      mspt,
      summary,
      stats,
      rankHistory,
      standing,
      siblings,
      catalog,
      siblingFqdns,
      loading,
      error,
      loadedAt,
      notFound,
      refresh,
    ],
  );
}
